import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./MobileOtpPage.css";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import callApi from "../../../../utility/apiCaller";
import { setUserClickData } from "../../../../utility/setUserClickData";
import { login } from "../../../../store/app/appReducer";
import { useNavigate } from "react-router-dom";
import OTPInput from "react-otp-input";
import FormInputStyle2 from "../../../../components/Form/FormInputStyle2";
import FormButtonStyleCheckOffer from "../../../../components/Form/FormButtonStyleCheckOffer";
import { setInternalLead } from "../../../../store/app/aaSlice";

// ─── Lender config (single source of truth) ───────────────────────────────────
const PREAPPROVED_LENDERS = [
  { lender_id: "662752eb65fdba1a48d6e464", lender_name: "MoneyView" },
  { lender_id: "6851271c3a9753dd00d85475", lender_name: "Fatakpay" },
  { lender_id: "662752eb65fdba1a48d6e466", lender_name: "KreditBee" },
  { lender_id: "68b7cb3721c19b150d2893da", lender_name: "Creditsea" },
];

const RESEND_OTP_TIME = 30;

const MobileOtpPage = ({
  isFormStarted,
  setIsFormStarted,
  currentStep,
  setCurrentStep,
  formData,
  setFormData,
}) => {
  const [mobile, setMobile] = useState("");
  const [isMobileValid, setIsMobileValid] = useState(true);
  const [source, setSource] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [affId, setAffId] = useState("");
  const [params] = useSearchParams();
  const [isConsentChecked, setIsConsentChecked] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isTncChecked, setIsTncChecked] = useState(true);
  const [isOtpGenerated, setIsOtpGenerated] = useState(false);
  const [otp, setOtp] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isEditMobileModalOpen, setIsEditMobileModalOpen] = useState(false);
  const [editMobileValue, setEditMobileValue] = useState("");
  const [editMobileError, setEditMobileError] = useState("");
  const [resendCounter, setResendCounter] = useState(RESEND_OTP_TIME);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [otpTimerKey, setOtpTimerKey] = useState(0);
  const [ipAddress, setIpAddress] = useState("");
  const [CuserName, setUserName] = useState("");

  const ldId = params.get("ld_id") || "";

  // ── Fetch IP ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        setIpAddress(data?.ip);
      } catch (error) {
        console.log("Error fetching IP:", error);
      }
    };
    fetchIp();
  }, []);

  // ── OTP countdown timer ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isOtpGenerated) return;
    setResendCounter(RESEND_OTP_TIME);
    setCanResendOtp(false);
    const interval = setInterval(() => {
      setResendCounter((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResendOtp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isOtpGenerated, otpTimerKey]);

  // ── URL params ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (params.get("source")) setSource(params.get("source"));
    if (params.get("utm_source")) setUtmSource(params.get("utm_source"));
    if (params.get("utm_medium")) setUtmMedium(params.get("utm_medium"));
    if (params.get("aff_id")) setAffId(params.get("aff_id"));
  }, [params]);

  // ── Expose reset handle for parent ──────────────────────────────────────
  useEffect(() => {
    const container = document.getElementById("personal-loan-form");
    if (container) {
      container.resetOtpState = () => {
        setIsOtpGenerated(false);
        setOtp("");
      };
    }
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatResendTime = (seconds) => {
    const clamped = Math.max(0, Number(seconds) || 0);
    const mm = String(Math.floor(clamped / 60)).padStart(2, "0");
    const ss = String(clamped % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const handleMobileChange = (event) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) {
      setMobile(value);
      setFormData((prev) => ({ ...prev, mobile: value }));
    }
    setIsMobileValid(value.length === 10);
  };

  const handleConsentChange = () => setIsConsentChecked((prev) => !prev);

  const handleValidation = () => {
    if (!/^\d{10}$/.test(mobile)) {
      setIsMobileValid(false);
      return false;
    }
    setIsMobileValid(true);
    return true;
  };

  // ── STEP A: Check if mobile exists in DB ─────────────────────────────────
  // Returns the lead_id string if found, null if not found.
  const checkMobileInDB = async () => {
    try {
      const response = await callApi(
        "v1/lead/list",
        "post",
        {
          filters: { contact_phone: mobile },
        },
        "core",
      );

      const leads = response?.data?.leadList || [];

      if (leads.length === 0) {
        // ✅ Mobile NOT in DB → redirect to personal loan page
        return null;
      }

      // Sort descending by updatedAt / createdAt to get the most recent lead
      const sortedLeads = [...leads].sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) -
          new Date(a.updatedAt || a.createdAt),
      );

      return sortedLeads[0]._id;
    } catch (error) {
      console.error("DB check failed:", error);
      return null;
    }
  };

  // ── STEP B: Push lead to all 4 lenders ──────────────────────────────────
  const pushToLenders = async (leadId, token) => {
    const results = await Promise.allSettled(
      PREAPPROVED_LENDERS.map((lender) =>
        callApi(
          "v1/lead/bulk-lead-push-by-leadId",
          "post",
          {
            leads: [{ lead_id: ldId }],
            lender_name: lender.lender_name,
            lender_id: lender.lender_id,
          },
          "core",
          token,
        ),
      ),
    );

    // Log any failures but don't block the flow
    results.forEach((result, idx) => {
      if (result.status === "rejected") {
        console.error(
          `Lender push failed for ${PREAPPROVED_LENDERS[idx].lender_name}:`,
          result.reason,
        );
      }
    });
  };

  // ── Send OTP ─────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!handleValidation()) return;
    if (!isConsentChecked) {
      toast.error("Please agree to the Terms and Conditions.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await callApi(
        "v1/sms/send-otp",
        "post",
        { contact_phone: mobile, kyc_consent: isConsentChecked },
        "messaging",
      );

      if (response.status === "Success") {
        setUserClickData({
          event_name: "otp-send-for-multilenders-pre-lead",
          user_id: ldId || "unknown",
          affiliate_id: affId || "No Aff_id found",
          utm_medium: utmMedium || "",
        });
        setFormData((prev) => ({
          ...prev,
          mobile,
          mobiletncchecked: true,
          mobiletnctime: new Date().toISOString(),
          stepDone: 3,
        }));
        toast.success("OTP Sent Successfully");
        setIsOtpGenerated(true);
        setIsFormStarted();
        setOtpTimerKey((prev) => prev + 1);
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while sending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Edit mobile modal handlers ────────────────────────────────────────────
  const openEditMobileModal = () => {
    setEditMobileValue(mobile || "");
    setEditMobileError("");
    setIsEditMobileModalOpen(true);
  };

  const closeEditMobileModal = () => {
    setIsEditMobileModalOpen(false);
    setEditMobileError("");
  };

  const handleEditMobileSendOtp = async () => {
    const nextMobile = String(editMobileValue || "").trim();
    if (!/^\d{10}$/.test(nextMobile)) {
      setEditMobileError("Please enter a valid Mobile Number");
      return;
    }
    try {
      setIsLoading(true);
      const response = await callApi(
        "v1/sms/send-otp",
        "post",
        { contact_phone: nextMobile, kyc_consent: isConsentChecked },
        "messaging",
      );
      if (response.status === "Success") {
        setMobile(nextMobile);
        setFormData((prev) => ({ ...prev, mobile: nextMobile }));
        setOtp("");
        setIsOtpGenerated(true);
        setOtpTimerKey((prev) => prev + 1);
        closeEditMobileModal();
        toast.success("OTP Sent Successfully");
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while sending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    try {
      setIsLoading(true);
      const response = await callApi(
        "v1/sms/send-otp",
        "post",
        {
          contact_phone: mobile,
          kyc_consent: isConsentChecked,
          kyc_consent_datetime: new Date().toISOString(),
        },
        "messaging",
      );
      if (response.status === "Success") {
        setUserClickData({
          event_name: "resend-otp-form-for-multilenders-pre-lead",
          user_id: ldId || "unknown",
          affiliate_id: affId || "No Aff_id found",
        });
        toast.success("OTP Resent Successfully");
        setOtpTimerKey((prev) => prev + 1);
      } else {
        toast.error("Failed to resend OTP.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while resending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Verify OTP — MAIN FLOW ────────────────────────────────────────────────
  const handleOtpSubmit = async () => {
    if (otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP.");
      return;
    }

    try {
      setIsLoading(true);

      // ── 1. Validate OTP ────────────────────────────────────────────────
      const otpResponse = await callApi(
        "v1/sms/validate-otp",
        "post",
        { contact_phone: mobile, otp },
        "messaging",
      );

      if (otpResponse.status !== "Success") {
        toast.error("Invalid OTP. Please try again.");
        return;
      }

      setUserClickData({
        event_name: "verify-otp-multilenders-pre-lead",
        user_id: ldId || "unknown",
        affiliate_id: affId || "No Aff_id found",
        utm_source: utmSource || "",
        utm_medium: utmMedium || "",
      });
      toast.success("OTP Verified Successfully");

      // ── 2. Login the user ──────────────────────────────────────────────
      dispatch(
        login({
          ...otpResponse.data.customer,
          token: otpResponse.data.token,
        }),
      );
      localStorage.setItem("mobile", mobile);

      // ── 3. Check if mobile exists in DB ───────────────────────────────
      //    ✅ EXISTS   → push to 4 lenders → show offer page
      //    ❌ NOT FOUND → redirect to /personal-loan
      const existingLeadId = await checkMobileInDB();

      if (!existingLeadId) {
        // Mobile NOT found in DB → redirect to personal loan journey
        console.log("Mobile not found in DB, redirecting to /personal-loan");
        navigate("/personal-loan");
        return;
      }

      // Store internal lead id in Redux
      dispatch(setInternalLead(existingLeadId));

      // ── 4. Push lead to all 4 lenders in parallel ─────────────────────
      await pushToLenders(existingLeadId, otpResponse.data.token);

      // ── 5. Move to offers page ─────────────────────────────────────────
      setCurrentStep(2);
    } catch (otpError) {
      console.error("Error occurred while verifying OTP:", otpError);
      toast.error("An error occurred while verifying OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {!isOtpGenerated ? (
        // ── Mobile input screen ──────────────────────────────────────────
        <div className="lead-capture-container1">
          <input type="hidden" name="utm_campaign" value="" />
          <input type="hidden" name="utm_source" value={utmSource || ""} />
          <input type="hidden" name="utm_medium" value="" />
          <input type="hidden" name="utm_content" value="" />
          <input type="hidden" name="click_id" value="" />
          <input type="hidden" name="aff_id" value={affId || ""} />
          <input type="hidden" name="src" value={source || ""} />

          <FormInputStyle2
            type="text"
            name="mobile"
            isValid={isMobileValid}
            id="mobile"
            minLength="10"
            maxLength="10"
            pattern="[0-9]{10}"
            value={mobile}
            onChange={handleMobileChange}
            required
            label={"Mobile Number"}
            inputMode="numeric"
            errorMessage={"Please enter a valid Mobile Number"}
          />

          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="consent"
              checked={isConsentChecked}
              onChange={handleConsentChange}
            />
            <label htmlFor="consent" className="consent">
              I have read and agreed to the{" "}
              <a className="anc-font" href="https://digitmoney.in/terms">
                Terms of Use
              </a>{" "}
              and hereby appoint DigitMoney and its{" "}
              <a className="anc-font" href="https://digitmoney.in/lenders">
                Lending Partners
              </a>{" "}
              to receive my credit information from credit bureaus. By
              submitting my details I override my NDNC registration &amp;
              authorize DigitMoney and its{" "}
              <a className="anc-font" href="https://digitmoney.in/lenders">
                Lending Partners
              </a>{" "}
              / representatives to contact me through Call, SMS, Email, WhatsApp
              or any other mode. I also Authorize you to send me new promotional
              offers of financial &amp; non-financial products or services from
              time to time.
              <br />
              <br />
              You will receive OTP on mobile number
            </label>
          </div>

          <FormButtonStyleCheckOffer
            style={{ backgroundColor: "#12F280" }}
            text="Get OTP"
            onClick={handleSendOtp}
            disabled={!isMobileValid || isLoading}
            id="btn-get-otp-landing-v4"
            className="tracking-btn-get-otp-landing-v4"
          />
        </div>
      ) : (
        // ── OTP verification screen ────────────────────────────────────
        <div className="plv16-otp-page">
          <div className="plv16-otp-hero">
            <img
              src="/assets/img/Layer_1 (47).png"
              className="plv16-otp-lock"
              alt="Lock"
            />
            <h1 className="plv16-otp-title">Verify number securely</h1>
            <p className="plv16-otp-subtitle">
              Your details are safe with us
              <img
                src="/assets/img/Layer_1 (48).png"
                className="plv16-otp-info"
                alt="Secure"
              />
            </p>
          </div>

          <div className="plv16-otp-card">
            <div className="plv16-otp-card-title">
              We can help prefill your information
            </div>

            <div className="plv16-otp-card-subtitle">
              Enter OTP sent to{" "}
              <span className="plv16-otp-phone">
                +91-{mobile || "98616XXXXX"}
              </span>{" "}
              <button
                type="button"
                className="plv16-otp-edit"
                onClick={openEditMobileModal}
              >
                Edit
              </button>
            </div>

            <div className="plv16-otp-input-wrap">
              <OTPInput
                value={otp}
                onChange={(val) => setOtp(val)}
                numInputs={4}
                isInputNum={true}
                shouldAutoFocus={true}
                inputStyle="plv16-otp-input"
                containerStyle="plv16-otp-inputs"
                placeholder={"----"}
                renderInput={(props) => <input {...props} />}
              />
            </div>

            <div className="plv16-otp-consent-row">
              <input
                type="checkbox"
                id="plv16-otp-consent"
                checked={isConsentChecked}
                onChange={handleConsentChange}
              />
              <label
                htmlFor="plv16-otp-consent"
                className="plv16-otp-consent-text"
              >
                I have read and agreed to the{" "}
                <a href="https://digitmoney.in/terms">Terms of Use</a> and
                hereby appoint DigitMoney and its{" "}
                <a href="https://digitmoney.in/lenders">Lending Partners</a> to
                receive my credit information from credit bureaus. By submitting
                my details I override my NDNC registration and authorize
                DigitMoney and its Lending Partners to contact me.
              </label>
            </div>
          </div>

          <div className="plv16-otp-footer">
            <div className="plv16-otp-resend">
              {canResendOtp ? (
                <button
                  type="button"
                  className="plv16-otp-resend-link"
                  onClick={handleResendOtp}
                  disabled={isLoading}
                >
                  Resend OTP
                </button>
              ) : (
                <>
                  Resend OTP in <span>{formatResendTime(resendCounter)}</span>
                </>
              )}
            </div>

            <button
              type="button"
              className="plv16-otp-btn"
              onClick={handleOtpSubmit}
              disabled={otp.length !== 4 || isLoading}
              id="btn-verify-otp-personal-details-v9"
            >
              {isLoading ? "Processing..." : "Verify OTP"}
            </button>
          </div>

          {/* Edit Mobile Modal */}
          {isEditMobileModalOpen && (
            <div
              className="plv16-otp-modal-overlay"
              role="dialog"
              aria-modal="true"
              onClick={closeEditMobileModal}
            >
              <div
                className="plv16-otp-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="plv16-otp-modal-header">
                  <button
                    type="button"
                    className="plv16-otp-modal-close"
                    onClick={closeEditMobileModal}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="plv16-otp-modal-title">Edit Mobile Number</div>
                <div className="plv16-otp-modal-subtitle">
                  We'll send a new OTP to this number.
                </div>

                <div className="plv16-otp-modal-body">
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter New Mobile Number"
                    className="plv16-otp-modal-input"
                    value={editMobileValue}
                    onChange={(e) => {
                      const next = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setEditMobileValue(next);
                      setEditMobileError("");
                    }}
                  />
                  {editMobileError && (
                    <div className="plv16-otp-modal-error">{editMobileError}</div>
                  )}
                  <button
                    type="button"
                    className="plv16-otp-modal-btn"
                    onClick={handleEditMobileSendOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "Processing..." : "Send OTP"}
                  </button>
                  <div className="plv16-otp-modal-footnote">
                    Make sure this number is active to receive the OTP.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MobileOtpPage;